import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Section } from '../../models';
import './insights-review-notice.css';

/**
 * The Insights panels whose DESIGN is settled but whose CONTENT (thresholds,
 * pattern wording, how readily a gauge is willing to say "likely") has not
 * been through clinical review yet. That review is exactly why each is held
 * back from prod, so this list tracks the env gates in
 * bloodwork-test-analysis-section.tsx; see docs/feature-visibility.md.
 *
 * Glucose is deliberately absent: it is reviewed and live in prod. RBC and WBC
 * likewise. When one of these four passes review and ships, drop it from the
 * list at the same time as its gate.
 */
const SECTIONS_PENDING_CLINICAL_REVIEW: Section[] = [
  Section.THYROID,
  // Iron Insights (2026-09-20): the pattern set is the RBC section's existing
  // iron rules read on their own, but the gauge ladder and the standalone
  // reading are new and unreviewed.
  Section.IRON,
  // Hydration Insights.
  Section.ELECTROLYTES,
  // Lipid Particle Insights (2026-10-23). Phase 1 is the Particle Map hero
  // only, and two things behind it are unreviewed: the four proposed upper
  // range bounds, and the cell naming ("More and Smaller" against the lab's own
  // "Pattern B"). Screen-only until both are settled, which also keeps it out
  // of the client PDF while the five area bars and nineteen pattern boxes are
  // still missing.
  Section.LIPIDS,
  // Nutrients (NUTRIENTS) and STRESS_ADRENAL left this list on
  // 2026-08-31: both passed clinical review and released to prod, so their
  // panels print to the client PDF without the design-preview notice.
];

export const insightsPendingClinicalReview = (section: Section) =>
  SECTIONS_PENDING_CLINICAL_REVIEW.includes(section);

/**
 * Whether this section's Insights panel may print at all.
 *
 * A panel pending clinical review is SCREEN-ONLY — it does not print in any
 * build, not even the ones that show it (2026-08-31). The practitioner reviewing
 * a design preview wants it on screen; the PDF is the client's document, and a
 * card stamped "pending clinical review" does not belong in one. Holding it back
 * on env alone left it printing on slava, notice and all.
 *
 * Reviewed panels (Glucose, RBC, WBC, Nutrients, Stress & Adrenal)
 * print everywhere, as they always have. A panel joins them by leaving
 * SECTIONS_PENDING_CLINICAL_REVIEW, which is the same edit that drops its
 * notice — so shipping a panel and printing it are one decision.
 */
export const insightsPanelPrintable = (section: Section) =>
  !insightsPendingClinicalReview(section);

/**
 * What the notice costs the PDF page, for the pagination estimates in
 * pdf-section.tsx and bloodwork-analysis-pdf.tsx. Sized like
 * PREVIOUS_COLLECTION_CAPTION_PDF_HEIGHT: ONE 12px/1.4 line inside the 300px
 * panel column (17), plus 12 of padding, 2 of border and the 8 margin below it.
 * It was 56 while the sentence wrapped to two lines.
 */
export const INSIGHTS_REVIEW_NOTICE_PDF_HEIGHT = 39;

/**
 * Practitioner-facing flag above an unreviewed Insights panel, so a panel is
 * never mistaken for a released finding.
 *
 * The wording is held to ONE line inside the 300px insights column (measured
 * 2026-10-28 at 248.9px of the 260px available at 12px). A second line pushes
 * the panel visibly further below the Results panel beside it, which is what
 * "Content is pending clinical review." used to do at 291.3px.
 *
 * It prints in the PDF as well as on screen. The PDF is normally the client's
 * document and this is a note about our own release state, but the panels it
 * sits above only exist in dev and slava (see insightsPanelPrintable), and a
 * test export of an unvalidated pattern is exactly the copy that most needs to
 * say so.
 */
export const InsightsReviewNotice: React.FC = () => (
  <div className="insights-review-notice">
    <FontAwesomeIcon icon="info-circle" className="insights-review-notice-icon" />
    <span>
      <strong>Design preview.</strong> Content is pending review.
    </span>
  </div>
);
